import type {
  User,
  Tour,
  Booking,
  Message,
  Notification,
  CreateTourData,
  CreateBookingData,
  PaginatedResponse,
  AdminStats,
  AuditLog,
  HealthStatus,
  TourAvailabilityRecurring,
  TourAvailabilitySpecific,
  GuideWhitelistEntry,
  AdminWhitelistEntry,
  GuideApplication,
  Currency,
  CurrencyConversion,
  ExchangeRates,
} from "@/types"

const API_BASE = import.meta.env.VITE_API_URL || "/api"

class ApiError extends Error {
  status: number
  details?: Record<string, string[]>

  constructor(message: string, status: number, details?: Record<string, string[]>) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.details = details
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }))
    throw new ApiError(
      error.message || error.error || `HTTP ${response.status}`,
      response.status,
      error.details
    )
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T
  }

  return response.json()
}

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<{ success: boolean }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  register: (data: { email: string; password: string; name: string; role: string }) =>
    request<{ success: boolean }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  logout: () =>
    request<{ success: boolean }>("/auth/logout", { method: "POST" }),

  getMe: () => request<User>("/auth/me"),

  // Tours
  getTours: (params?: URLSearchParams) =>
    request<Tour[]>(`/tours${params ? `?${params.toString()}` : ""}`),

  getTour: (id: string) => request<Tour>(`/tours/${id}`),

  createTour: (data: CreateTourData) =>
    request<Tour>("/tours", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateTour: (id: string, data: Partial<CreateTourData>) =>
    request<Tour>(`/tours/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteTour: (id: string) =>
    request<{ success: boolean }>(`/tours/${id}`, { method: "DELETE" }),

  getMyTours: () => request<Tour[]>("/tours/mine"),

  getTourAvailability: (tourId: string) =>
    request<{
      recurring: TourAvailabilityRecurring[]
      specific: TourAvailabilitySpecific[]
    }>(`/tours/${tourId}/availability`),

  addTourAvailability: (
    tourId: string,
    data: Omit<TourAvailabilityRecurring, "id" | "tourId"> | Omit<TourAvailabilitySpecific, "id" | "tourId">
  ) =>
    request<TourAvailabilityRecurring | TourAvailabilitySpecific>(
      `/tours/${tourId}/availability`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    ),

  removeTourAvailability: (tourId: string, slotId: string) =>
    request<{ success: boolean }>(`/tours/${tourId}/availability/${slotId}`, {
      method: "DELETE",
    }),

  // Bookings
  getBookings: () => request<Booking[]>("/bookings"),

  getBooking: (id: string) => request<Booking>(`/bookings/${id}`),

  createBooking: (data: CreateBookingData) =>
    request<Booking>("/bookings", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateBookingStatus: (id: string, status: string) =>
    request<Booking>(`/bookings/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    }),

  // Messages
  getMessages: (bookingId: string) =>
    request<Message[]>(`/bookings/${bookingId}/messages`),

  sendMessage: (bookingId: string, content: string) =>
    request<Message>(`/bookings/${bookingId}/messages`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  markMessageRead: (messageId: string) =>
    request<{ success: boolean }>(`/messages/${messageId}/read`, {
      method: "PUT",
    }),

  // Notifications
  getNotifications: () => request<Notification[]>("/notifications"),

  getUnreadCount: () =>
    request<{ count: number }>("/notifications/unread-count"),

  markNotificationRead: (id: string) =>
    request<{ success: boolean }>(`/notifications/${id}/read`, {
      method: "PUT",
    }),

  markAllNotificationsRead: () =>
    request<{ success: boolean }>("/notifications/read-all", {
      method: "PUT",
    }),

  // User profile
  updateProfile: (data: { name?: string; bio?: string; phone?: string; avatar?: string }) =>
    request<User>("/users/profile", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ success: boolean }>("/users/password", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  // Admin endpoints
  admin: {
    getStats: () => request<AdminStats>("/admin/stats"),

    getUsers: (params?: URLSearchParams) =>
      request<PaginatedResponse<User>>(
        `/admin/users${params ? `?${params.toString()}` : ""}`
      ),

    getUser: (id: string) => request<User>(`/admin/users/${id}`),

    updateUserStatus: (id: string, status: "active" | "suspended") =>
      request<User>(`/admin/users/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      }),

    updateUserRole: (id: string, role: "customer" | "guide" | "admin") =>
      request<User>(`/admin/users/${id}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      }),

    getGuides: (params?: URLSearchParams) =>
      request<PaginatedResponse<User & { tourCount: number; bookingCount: number }>>(
        `/admin/guides${params ? `?${params.toString()}` : ""}`
      ),

    toggleGuideFeatured: (id: string, featured: boolean) =>
      request<User>(`/admin/guides/${id}/featured`, {
        method: "PUT",
        body: JSON.stringify({ featured }),
      }),

    getTours: (params?: URLSearchParams) =>
      request<PaginatedResponse<Tour>>(
        `/admin/tours${params ? `?${params.toString()}` : ""}`
      ),

    toggleTourFeatured: (id: string, featured: boolean) =>
      request<Tour>(`/admin/tours/${id}/featured`, {
        method: "PUT",
        body: JSON.stringify({ featured }),
      }),

    deleteTour: (id: string) =>
      request<{ success: boolean }>(`/admin/tours/${id}`, { method: "DELETE" }),

    getBookings: (params?: URLSearchParams) =>
      request<PaginatedResponse<Booking>>(
        `/admin/bookings${params ? `?${params.toString()}` : ""}`
      ),

    getHealth: () => request<HealthStatus>("/admin/health"),

    getLogs: (params?: URLSearchParams) =>
      request<PaginatedResponse<AuditLog>>(
        `/admin/logs${params ? `?${params.toString()}` : ""}`
      ),

    // Guide whitelist management
    getGuideWhitelist: (params?: URLSearchParams) =>
      request<PaginatedResponse<GuideWhitelistEntry>>(
        `/admin/guide-whitelist${params ? `?${params.toString()}` : ""}`
      ),

    addToGuideWhitelist: (email: string, note?: string) =>
      request<{ success: boolean; id: string }>("/admin/guide-whitelist", {
        method: "POST",
        body: JSON.stringify({ email, note }),
      }),

    removeFromGuideWhitelist: (id: string) =>
      request<{ success: boolean }>(`/admin/guide-whitelist/${id}`, {
        method: "DELETE",
      }),

    // Admin whitelist management (owner only)
    getAdminWhitelist: (params?: URLSearchParams) =>
      request<PaginatedResponse<AdminWhitelistEntry>>(
        `/admin/admin-whitelist${params ? `?${params.toString()}` : ""}`
      ),

    addToAdminWhitelist: (email: string, note?: string) =>
      request<{ success: boolean; id: string }>("/admin/admin-whitelist", {
        method: "POST",
        body: JSON.stringify({ email, note }),
      }),

    removeFromAdminWhitelist: (id: string) =>
      request<{ success: boolean }>(`/admin/admin-whitelist/${id}`, {
        method: "DELETE",
      }),

    // Guide applications
    getGuideApplications: (params?: URLSearchParams) =>
      request<PaginatedResponse<GuideApplication>>(
        `/admin/guide-applications${params ? `?${params.toString()}` : ""}`
      ),

    updateGuideApplication: (id: string, status: "approved" | "rejected") =>
      request<{ success: boolean }>(`/admin/guide-applications/${id}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      }),
  },

  // Demo login
  demoLogin: (account: "owner" | "admin" | "guide" | "guide2" | "user") =>
    request<{ success: boolean; message: string; user: { id: string; name: string; role: string } }>("/auth/demo/login", {
      method: "POST",
      body: JSON.stringify({ account }),
    }),

  getDemoAccounts: () =>
    request<{
      accounts: Array<{ id: string; email: string; name: string; role: string; description: string }>
      note: string
    }>("/auth/demo/accounts"),

  // Currencies
  getCurrencies: () => request<Currency[]>("/currencies"),

  getCurrency: (code: string) => request<Currency>(`/currencies/${code}`),

  convertCurrency: (from: string, to: string, amount: number) =>
    request<CurrencyConversion>(`/currencies/convert?from=${from}&to=${to}&amount=${amount}`),

  getExchangeRates: (base: string) =>
    request<ExchangeRates>(`/currencies/rates/${base}`),
}

export { ApiError }
